import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import DetailHero from "./components/DetailHero";
import ReviewSection from "./components/ReviewSection";
import { reviewList } from "@/mocks/reviews";
import { getMediaById, getRelated, categoryName } from "@/lib/media";

const container = "mx-auto w-full max-w-[1280px] px-4 md:px-6";

export default function Detail() {
  const { id } = useParams();
  const item = getMediaById(id);

  useEffect(() => {
    document.title = item
      ? `${categoryName(item.category)}《${item.title}》详情介绍 - 红狐影视`
      : "作品不存在 - 红狐影视";
  }, [item]);

  if (!item) {
    return (
      <div className={container}>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <i className="ri-film-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">
            没有找到这部作品
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            该作品可能已下架或链接有误，去首页看看其他推荐吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-home-5-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelated(item, 6);
  const reviews = reviewList.filter((r) => r.mediaId === item.id);

  return (
    <div className="w-full">
      <DetailHero item={item} />

      <div className={`${container} py-6 md:py-8`}>
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: categoryName(item.category), to: `/category/${item.category}` },
            { label: item.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px]">
          <div className="min-w-0">
            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-file-text-line text-primary-500"></i>
                剧情简介
              </h2>
              <p className="mt-4 text-[15px] leading-loose text-foreground-700">
                {item.synopsis}
              </p>
            </section>

            <ReviewSection reviews={reviews} />
          </div>

          <aside className="min-w-0">
            <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                作品信息
              </h3>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  { icon: "ri-user-star-line", label: "导演", value: item.director },
                  {
                    icon: "ri-group-line",
                    label: "主演",
                    value: item.cast.join(" / "),
                  },
                  { icon: "ri-price-tag-3-line", label: "类型", value: item.genres.join(" / ") },
                  { icon: "ri-earth-line", label: "地区", value: item.region },
                  { icon: "ri-calendar-line", label: "上映", value: item.releaseDate },
                ].map((row) => (
                  <li key={row.label} className="flex gap-3">
                    <i className={`${row.icon} mt-0.5 text-primary-500`}></i>
                    <span className="w-10 shrink-0 text-foreground-500">
                      {row.label}
                    </span>
                    <span className="min-w-0 flex-1 text-foreground-800">
                      {row.value}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                热度指数
              </h3>
              <div className="mt-4 flex items-end justify-between">
                <span className="font-heading text-3xl font-extrabold text-primary-600">
                  {item.heat}
                </span>
                <span className="text-xs text-foreground-500">满分 100</span>
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-background-200">
                <div
                  className="h-full rounded-full bg-primary-500"
                  style={{ width: `${item.heat}%` }}
                ></div>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-movie-2-line text-primary-500"></i>
            相关推荐
          </h2>
          <p className="mt-1 text-sm text-foreground-500">
            根据类型与题材为你关联的影视作品
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {related.map((r, i) => (
              <MediaCard key={r.id} item={r} index={i} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}